import React from 'react';
import { Link } from 'react-router-dom';
import { Cake, UserPlus, Award } from 'lucide-react';
import { appLinks, events, birthdays, newHires } from './data';
import { useApp } from './store';
function Card({
  title,
  children,
  action,
  actionTo
}: {
  title: string;
  children: React.ReactNode;
  action?: string;
  actionTo?: string;
}) {
  return <div className="bg-white rounded-xl border border-navy-100 p-5" data-id="element-906" data-mp="25o8v0p7k76jd">
      <div className="flex items-center justify-between mb-4" data-id="element-907" data-mp="2kdy9tn8kr924">
        <h3 className="text-base font-serif font-bold text-navy-900" data-id="element-908" data-mp="2fgeusl41ys94">
          {title}
        </h3>
        {action && actionTo && <Link to={actionTo} className="text-xs font-semibold text-gold-600 hover:text-gold-700 uppercase tracking-wide" data-id="element-909" data-mp="2temqyqt9lpj8">
            {action}
          </Link>}
      </div>
      {children}
    </div>;
}
export function RightRail() {
  const {
    kudos
  } = useApp();
  return <aside className="space-y-6" aria-label="Highlights" data-id="element-910" data-mp="v24jfhynbjzm">
      {/* App launcher */}
      <Card title="Your Apps" action="All" actionTo="/apps" data-id="element-911" data-mp="3lvlyt85jnqj2">
        <div className="grid grid-cols-3 gap-3" data-id="element-912" data-mp="3qskq7ixtpjdk">
          {appLinks.slice(0, 6).map(app => <Link to="/apps" key={app.label} className="flex flex-col items-center gap-1.5 group" data-id="element-913" data-mp="3vrp0kcwhhn5h">
              <span className={`w-12 h-12 rounded-xl ${app.color} text-white font-bold text-sm flex items-center justify-center group-hover:scale-105 transition-transform shadow-sm`} data-id="element-914" data-mp="4euf2d8lmbkh">
                {app.initials}
              </span>
              <span className="text-[0.7rem] text-navy-600 font-medium" data-id="element-915" data-mp="iec6l0da42xj">
                {app.label}
              </span>
            </Link>)}
        </div>
      </Card>

      {/* Events */}
      <Card title="Upcoming Events" action="Calendar" actionTo="/events" data-id="element-916" data-mp="2k5p8dgjy9upd">
        <ul className="space-y-3" data-id="element-917" data-mp="2p38koax37xeo">
          {events.slice(0, 3).map(e => <li key={e.id} className="flex items-center gap-3" data-id="element-918" data-mp="2h32nhwha0k3o">
              <div className="w-12 shrink-0 text-center bg-navy-50 rounded-lg py-1.5" data-id="element-919" data-mp="2h3ce9ma5avaf">
                <div className="text-base font-bold text-navy-900 leading-none" data-id="element-920" data-mp="2d31wlcczqgfq">
                  {e.day}
                </div>
                <div className="text-[0.6rem] font-bold text-gold-600 tracking-wide" data-id="element-921" data-mp="2qyvvd2k1vpf4">
                  {e.month}
                </div>
              </div>
              <div className="min-w-0" data-id="element-922" data-mp="300vfw4u9xbmt">
                <p className="text-sm font-semibold text-navy-900 leading-snug truncate" data-id="element-923" data-mp="2zzu4u0d9cp53">
                  {e.title}
                </p>
                <p className="text-xs text-navy-400" data-id="element-924" data-mp="39s9iilhiurk6">
                  {e.time} · {e.type}
                </p>
              </div>
            </li>)}
        </ul>
      </Card>

      {/* Recognition */}
      <Card title="Recognition" action="Send" actionTo="/recognition" data-id="element-925" data-mp="3jp4dwvii0bu8">
        <ul className="space-y-3" data-id="element-926" data-mp="3er2yckka6uni">
          {kudos.slice(0, 2).map(r => <li key={r.id} className="flex gap-3 text-sm" data-id="element-927" data-mp="3eozxt6li5j4j">
              <div className="w-8 h-8 shrink-0 rounded-full bg-gold-100 flex items-center justify-center text-gold-600" data-id="element-928" data-mp="28jrzadvovoa">
                <Award className="w-4 h-4" data-id="element-929" data-mp="680vvzdnpkrc" />
              </div>
              <p className="text-navy-700 leading-snug" data-id="element-930" data-mp="3oddmovhmxhrn">
                <span className="font-semibold text-navy-900" data-id="element-931" data-mp="3tdjimnuxtqfj">{r.from}</span>{' '}
                recognized{' '}
                <span className="font-semibold text-navy-900" data-id="element-932" data-mp="3eovp0n4njqs8">{r.to}</span>{' '}
                {r.message}
              </p>
            </li>)}
        </ul>
      </Card>

      {/* People */}
      <Card title="People" data-id="element-933" data-mp="35o7dbz2qe2ii">
        <div className="space-y-4" data-id="element-934" data-mp="35p096505j7lv">
          <div data-id="element-935" data-mp="35lulmu768ds9">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-navy-400 mb-2" data-id="element-936" data-mp="3an1s8pxkv6xc">
              <Cake className="w-3.5 h-3.5 text-gold-500" data-id="element-937" data-mp="2vq18b8lmog9f" /> Birthdays
            </p>
            <ul className="space-y-2" data-id="element-938" data-mp="30ouox9w7ondt">
              {birthdays.map(p => <li key={p.name} className="flex items-center gap-3" data-id="element-939" data-mp="30qyq3k2q78ae">
                  <img src={p.avatar} alt="" className="w-8 h-8 rounded-full object-cover" data-id="element-940" data-mp="7t2oro7xy2h1" />
                  <div className="min-w-0" data-id="element-941" data-mp="3u9fbd4n6g9xm">
                    <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-942" data-mp="3u84773ptl05z">
                      {p.name}
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-943" data-mp="3kbj08cu8pezi">{p.detail}</p>
                  </div>
                </li>)}
            </ul>
          </div>
          <div data-id="element-944" data-mp="3g8w4i0ug79x1">
            <p className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-navy-400 mb-2" data-id="element-945" data-mp="3gcjyt27pw0ct">
              <UserPlus className="w-3.5 h-3.5 text-sage" data-id="element-946" data-mp="36bzv7swq6jrf" /> New Hires
            </p>
            <ul className="space-y-2" data-id="element-947" data-mp="36eckkr4tbwei">
              {newHires.map(p => <li key={p.name} className="flex items-center gap-3" data-id="element-948" data-mp="19iy9i56xzyq9">
                  <img src={p.avatar} alt="" className="w-8 h-8 rounded-full object-cover" data-id="element-949" data-mp="14lodu7symqtz" />
                  <div className="min-w-0" data-id="element-950" data-mp="1h2z209rv02op">
                    <p className="text-sm font-semibold text-navy-900 truncate" data-id="element-951" data-mp="1h1o2im5g5h3e">
                      {p.name}
                    </p>
                    <p className="text-xs text-navy-400" data-id="element-952" data-mp="1qt26p78cf52v">{p.detail}</p>
                  </div>
                </li>)}
            </ul>
          </div>
        </div>
      </Card>
    </aside>;
}